import { useCallback, useMemo, useState, type FormEvent } from "react";
import { catchError, of, tap } from "rxjs";

import type {
  APILibraryTemplateMetadataRequestType,
  APILibraryTemplateResponseType,
} from "@/core/data_sources/models/APILibraryTemplate";
import { Button, TextInput } from "@/core/ui/atoms";
import { adminInputClasses, adminSelectClasses } from "@/core/ui/molecules";
import { useStore } from "@/core/utils/hooks";
import { ErrorResponse } from "@/core/utils/request";

import { LibraryTemplatesStore } from "../data_sources/store";

type TemplateType = Required<APILibraryTemplateResponseType>["type"];

type EditTemplateMetadataDialogProps = {
  readonly onCancel: () => void;
  readonly onSaved: () => void;
  readonly template: APILibraryTemplateResponseType;
};

export function EditTemplateMetadataDialog({
  onCancel,
  onSaved,
  template,
}: EditTemplateMetadataDialogProps) {
  const { actions } = useStore(LibraryTemplatesStore.instance);
  const [description, setDescription] = useState(template.description ?? "");
  const [error, setError] = useState("");
  const [name, setName] = useState(template.name ?? "");
  const [packs, setPacks] = useState((template.packs ?? []).join(", "));
  const [recommended, setRecommended] = useState(!!template.recommended);
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState<TemplateType>(template.type ?? "form");

  const canSubmit = useMemo(
    () => !!template.id && !!name.trim() && !saving,
    [name, saving, template.id],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit || !template.id) {
        setError("Template id and name are required.");
        return;
      }

      const metadata: APILibraryTemplateMetadataRequestType = {
        description: description.trim(),
        name: name.trim(),
        packs: packs
          .split(",")
          .map((pack) => pack.trim())
          .filter(Boolean),
        recommended,
        type,
      };

      setSaving(true);
      setError("");
      actions
        .updateTemplateMetadata(template.id, metadata)
        .pipe(
          tap(() => {
            onSaved();
          }),
          catchError((requestError) => {
            setError(
              requestError instanceof ErrorResponse
                ? requestError.error.message
                : requestError instanceof Error
                  ? requestError.message
                  : "Unable to save template metadata.",
            );
            return of(null);
          }),
          tap(() => setSaving(false)),
        )
        .subscribe();
    },
    [
      actions,
      canSubmit,
      description,
      name,
      onSaved,
      packs,
      recommended,
      template.id,
      type,
    ],
  );

  return (
    <form
      className="flex max-h-[calc(100vh-7.5rem)] w-[560px] max-w-full flex-col overflow-hidden"
      onSubmit={handleSubmit}
    >
      <div className="shrink-0 border-b border-border px-6 pb-3 pt-5">
        <h2 className="text-[17px] font-semibold text-ink">
          Edit template metadata
        </h2>
        <p className="mt-1 text-[13px] text-ink-subtle">
          Updates catalog metadata only. Template structure stays unchanged.
        </p>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-ink-muted">
            Name
          </span>
          <TextInput
            autoFocus
            onChange={(event) => setName(event.target.value)}
            value={name}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-ink-muted">
            Type
          </span>
          <select
            className={adminSelectClasses}
            onChange={(event) => setType(event.target.value as TemplateType)}
            value={type}
          >
            <option value="form">Form</option>
            <option value="media">Media</option>
            <option value="model">Model</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-ink-muted">
            Description
          </span>
          <textarea
            className={`${adminInputClasses} min-h-24 w-full py-2.5`}
            onChange={(event) => setDescription(event.target.value)}
            value={description}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-ink-muted">
            Used by packs
          </span>
          <TextInput
            onChange={(event) => setPacks(event.target.value)}
            placeholder="Pack A, Pack B"
            value={packs}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-ink-muted">
          <input
            checked={recommended}
            onChange={(event) => setRecommended(event.target.checked)}
            type="checkbox"
          />
          Recommended
        </label>
        {error ? <p className="text-xs text-critical">{error}</p> : null}
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t border-border bg-panel-muted px-6 py-3.5">
        <Button disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} type="submit" variant="primary">
          {saving ? "Saving..." : "Save metadata"}
        </Button>
      </div>
    </form>
  );
}
